import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PublicLayout from "@/layouts/PublicLayout";
import { Rocket, Users, ArrowRight, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LangContext";

/**
 * Sprint 92al — Public Projects page.
 * Shows every active WineTech project with the number of participating members.
 */
export default function ProjectsPage() {
  const { t } = useLang();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PublicLayout>
      <Seo
        title={t("proj_seo_title")}
        description={t("proj_seo_desc")}
      />
      <section className="relative overflow-hidden bg-gradient-to-br from-wine-900 via-wine-800 to-wine-900 py-20 text-white">
        <div className="mx-auto max-w-6xl px-4">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-xs font-semibold uppercase tracking-wider backdrop-blur">
            <Rocket className="h-3.5 w-3.5" /> {t("proj_badge")}
          </div>
          <h1 className="mt-5 text-4xl font-black leading-tight sm:text-5xl lg:text-6xl">
            {t("proj_h1_a")}<br/>
            <span className="text-pink-200">{t("proj_h1_b")}</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-white/80">
            {t("proj_intro")}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-wine-500" /></div>
        ) : projects.length === 0 ? (
          <div className="rounded-2xl bg-neutral-50 py-20 text-center text-neutral-500">
            <Rocket className="mx-auto h-10 w-10 text-neutral-300" />
            <p className="mt-3 text-sm" data-testid="projects-empty">{t("proj_empty")}</p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="public-projects-grid">
            {projects.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.slug}`}
                className="group overflow-hidden rounded-3xl border border-neutral-100 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl"
                data-testid={`project-card-${p.slug}`}
              >
                <div className="relative aspect-[16/9] bg-gradient-to-br from-wine-100 via-blush-100 to-pink-100">
                  {p.image && <img src={p.image} alt={p.title} className="h-full w-full object-cover" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between gap-2">
                    <span className="rounded-full bg-white/90 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-wine-700 backdrop-blur">🚀 {t("proj_tag")}</span>
                    {p.member_count > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-0.5 text-[11px] font-bold text-neutral-700 backdrop-blur">
                        <Users className="h-3 w-3" /> {p.member_count}
                      </span>
                    )}
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-bold text-navy group-hover:text-wine-700">{p.title}</h3>
                  {p.subtitle && <p className="mt-1 text-sm font-semibold text-pink-600">{p.subtitle}</p>}
                  {p.description && <p className="mt-2 line-clamp-3 text-sm text-neutral-600">{p.description}</p>}
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-wine-700 group-hover:gap-2 transition-all">
                    {t("proj_discover")} <ArrowRight className="h-3 w-3" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
